import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  Button,
  Alert,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { 
  CompletedWorkBilling, 
  BillingPeriod,
  completedWorkService
} from '../../services/newBillingService';

interface CompletedWorkViewProps {
  completedWork: CompletedWorkBilling[];
  billingPeriod: BillingPeriod;
  onRefresh: () => void;
}

const CompletedWorkView: React.FC<CompletedWorkViewProps> = ({
  completedWork,
  billingPeriod,
  onRefresh
}) => {
  const [selectedWork, setSelectedWork] = useState<number[]>([]);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [editingWork, setEditingWork] = useState<CompletedWorkBilling | null>(null);
  const [editHours, setEditHours] = useState('');
  const [editRate, setEditRate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const unbilledWork = completedWork.filter(work => !work.isBilled);
  const billedWork = completedWork.filter(work => work.isBilled);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedWork(unbilledWork.map(work => work.id));
    } else {
      setSelectedWork([]);
    }
  };

  const handleSelectWork = (workId: number, checked: boolean) => {
    if (checked) {
      setSelectedWork(prev => [...prev, workId]);
    } else {
      setSelectedWork(prev => prev.filter(id => id !== workId));
    }
  };

  const toggleRowExpansion = (workId: number) => {
    setExpandedRows(prev => 
      prev.includes(workId) 
        ? prev.filter(id => id !== workId)
        : [...prev, workId]
    );
  };

  const openEditDialog = (workItem: CompletedWorkBilling) => {
    setEditingWork(workItem);
    setEditHours(workItem.totalHours.toString());
    setEditRate(workItem.hourlyRate.toString());
    setEditNotes(workItem.workNotes || '');
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingWork) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const hours = parseFloat(editHours);
      const rate = parseFloat(editRate);
      if (isNaN(hours) || hours <= 0) { setEditError('Hours must be a positive number'); return; }
      if (isNaN(rate) || rate <= 0) { setEditError('Rate must be a positive number'); return; }
      await completedWorkService.updateCompletedWork(editingWork.id, {
        totalHours: hours,
        hourlyRate: rate,
        workNotes: editNotes,
        recalculateSegments: true
      });
      setEditingWork(null);
      onRefresh();
    } catch (err: any) {
      setEditError(err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatHours = (hours: number): string => {
    return `${hours.toFixed(2)}h`;
  };

  const WorkTable: React.FC<{ 
    work: CompletedWorkBilling[], 
    title: string, 
    showSelection?: boolean 
  }> = ({ work, title, showSelection = false }) => {
    if (work.length === 0) {
      return (
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Alert severity="info">
            No {title.toLowerCase()} found for this billing period.
          </Alert>
        </Box>
      );
    }

    return (
      <Box mb={4}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">
            {title} ({work.length})
          </Typography>
          {showSelection && work.length > 0 && (
            <Box display="flex" alignItems="center" gap={2}>
              <Checkbox
                checked={selectedWork.length === work.length}
                indeterminate={selectedWork.length > 0 && selectedWork.length < work.length}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
              <Typography variant="body2">
                Select All ({selectedWork.length} selected)
              </Typography>
            </Box>
          )}
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {showSelection && <TableCell padding="checkbox"></TableCell>}
                <TableCell></TableCell>
                <TableCell>Staff</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Shift Date</TableCell>
                <TableCell>Hours</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {work.map((workItem) => (
                <React.Fragment key={workItem.id}>
                  <TableRow>
                    {showSelection && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedWork.includes(workItem.id)}
                          onChange={(e) => handleSelectWork(workItem.id, e.target.checked)}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleRowExpansion(workItem.id)}
                      >
                        {expandedRows.includes(workItem.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                          {workItem.staffName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {workItem.employeeId}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {workItem.clientName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(workItem.shiftDate), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {formatHours(workItem.totalHours)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Reg: {formatHours(workItem.regularHours)}
                          {workItem.overtimeHours > 0 && `, OT: ${formatHours(workItem.overtimeHours)}`}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(workItem.totalAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={workItem.isBilled ? 'Billed' : 'Unbilled'}
                        color={workItem.isBilled ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell 
                      style={{ paddingBottom: 0, paddingTop: 0 }} 
                      colSpan={showSelection ? 8 : 7}
                    >
                      <Collapse in={expandedRows.includes(workItem.id)} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Shift Details
                          </Typography>
                          <Box display="flex" gap={4} flexWrap="wrap">
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Start Time
                              </Typography>
                              <Typography variant="body2">
                                {format(new Date(workItem.actualStartTime), 'HH:mm')}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                End Time
                              </Typography>
                              <Typography variant="body2">
                                {format(new Date(workItem.actualEndTime), 'HH:mm')}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Hourly Rate
                              </Typography>
                              <Typography variant="body2">
                                {formatCurrency(workItem.hourlyRate)}
                              </Typography>
                            </Box>
                            {workItem.overtimeRate && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Overtime Rate
                                </Typography>
                                <Typography variant="body2">
                                  {formatCurrency(workItem.overtimeRate)}
                                </Typography>
                              </Box>
                            )}
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Approved By
                              </Typography>
                              <Typography variant="body2">
                                User ID: {workItem.approvedBy}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Approved At
                              </Typography>
                              <Typography variant="body2">
                                {format(new Date(workItem.approvedAt), 'MMM dd, yyyy HH:mm')}
                              </Typography>
                            </Box>
                          </Box>
                          {workItem.approvalNotes && (
                            <Box mt={2}>
                              <Typography variant="caption" color="text.secondary">
                                Approval Notes
                              </Typography>
                              <Typography variant="body2">
                                {workItem.approvalNotes}
                              </Typography>
                            </Box>
                          )}
                          {workItem.workNotes && (
                            <Box mt={2}>
                              <Typography variant="caption" color="text.secondary">
                                Work Notes
                              </Typography>
                              <Typography variant="body2">
                                {workItem.workNotes}
                              </Typography>
                            </Box>
                          )}
                          {workItem.isBilled && workItem.billedAt && (
                            <Box mt={2}>
                              <Typography variant="caption" color="text.secondary">
                                Billed At
                              </Typography>
                              <Typography variant="body2">
                                {format(new Date(workItem.billedAt), 'MMM dd, yyyy HH:mm')}
                              </Typography>
                            </Box>
                          )}
                          {!workItem.isBilled && (
                            <Box mt={2}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => openEditDialog(workItem)}
                              >
                                Edit Hours &amp; Rate
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const totalUnbilledHours = unbilledWork.reduce((sum, work) => sum + work.totalHours, 0);
  const totalUnbilledAmount = unbilledWork.reduce((sum, work) => sum + work.totalAmount, 0);
  const totalBilledHours = billedWork.reduce((sum, work) => sum + work.totalHours, 0);
  const totalBilledAmount = billedWork.reduce((sum, work) => sum + work.totalAmount, 0);

  return (
    <Box>
      {/* Summary */}
      <Box mb={3} display="flex" gap={4} flexWrap="wrap">
        <Box>
          <Typography variant="h6" color="warning.main">
            Unbilled Work
          </Typography>
          <Typography variant="body1">
            {unbilledWork.length} shifts • {formatHours(totalUnbilledHours)} • {formatCurrency(totalUnbilledAmount)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="h6" color="success.main">
            Billed Work
          </Typography>
          <Typography variant="body1">
            {billedWork.length} shifts • {formatHours(totalBilledHours)} • {formatCurrency(totalBilledAmount)}
          </Typography>
        </Box>
      </Box>

      {/* Unbilled Work */}
      <WorkTable 
        work={unbilledWork} 
        title="Unbilled Work" 
        showSelection={true}
      />

      {/* Billed Work */}
      <WorkTable 
        work={billedWork} 
        title="Billed Work" 
      />

      {/* Edit Hours & Rate Dialog */}
      <Dialog open={!!editingWork} onClose={() => !editSaving && setEditingWork(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Hours &amp; Rate</DialogTitle>
        <DialogContent>
          {editingWork && (
            <Box pt={1} display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="text.secondary">
                {editingWork.staffName} — {format(new Date(editingWork.shiftDate), 'MMM dd, yyyy')}
              </Typography>
              {editError && <Alert severity="error">{editError}</Alert>}
              <TextField
                label="Total Hours"
                type="number"
                value={editHours}
                onChange={(e) => setEditHours(e.target.value)}
                inputProps={{ step: '0.25', min: '0.25' }}
                fullWidth
              />
              <TextField
                label="Hourly Rate (AUD)"
                type="number"
                value={editRate}
                onChange={(e) => setEditRate(e.target.value)}
                inputProps={{ step: '0.01', min: '0.01' }}
                fullWidth
              />
              <TextField
                label="Work Notes"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                multiline
                rows={2}
                fullWidth
              />
              <Typography variant="caption" color="text.secondary">
                Rates will be recalculated across morning/evening/night and weekend segments.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingWork(null)} disabled={editSaving}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={editSaving}>
            {editSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompletedWorkView;